/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { InputSwitch } from 'primereact/inputswitch';
import PageSkeleton from '../../components/common/page-skeleton';
import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import FormMultiSelect from '../../components/forms/form-multi-select';
import PhoneField from '../../components/forms/phone-field';
import PermissionGuard from '../../components/guards/PermissionGuard';
import ScreenHeader from '../../components/common/screen-header';
import { JOB_NAMES } from '../../constants/jobNames';
import { usersService, type GroupedUser, type UserGroupProjectInput } from '../../services/users.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';

const LANGUAGE_OPTIONS = [
  { value: '1', label: 'English' }, { value: '2', label: 'Telugu' },
  { value: '3', label: 'Hindi' }, { value: '4', label: 'Urdu' },
];

/** Editable per-project row state. */
interface ProjectRow {
  _id: string;
  appId: string;
  blocked: boolean;
  remove: boolean;
}

const UserManagementGroupForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [group, setGroup] = useState<GroupedUser | null>(null);
  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  const [personnelName, setPersonnelName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Shared identity
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState('');

  // Per-project rows + newly-added projects
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [addAppIds, setAddAppIds] = useState<string[]>([]);

  const isLinked = !!group?.personnelMasterId;

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const [g, accessible] = await Promise.all([
          usersService.getGroupDetail(id),
          projectsService.getAccessible().catch(() => []),
        ]);
        setGroup(g);
        setProjects(accessible);
        setEmail(g.email || '');
        setPhone(g.phoneNo || '');
        setLanguage(g.defaultlanguageId != null ? String(g.defaultlanguageId) : '');
        setRows(g.projects.map((p) => ({
          _id: p._id, appId: p.appId, blocked: !!p.blocked, remove: false,
        })));
        if (g.personnelMasterId) {
          const p = await personnelLookupService.getById(g.personnelMasterId).catch(() => null);
          setPersonnelName(p?.name || '');
        }
      } catch (err: any) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(err, 'Failed to load user'), life: 8000 });
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Projects the user is NOT already in — offered to add.
  const addOptions = useMemo(() => {
    const inGroup = new Set(rows.map((r) => r.appId));
    return projects
      .filter((p) => !inGroup.has(p._id))
      .map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name }));
  }, [projects, rows]);

  const setRow = (appId: string, patch: Partial<ProjectRow>) =>
    setRows((rs) => rs.map((r) => (r.appId === appId ? { ...r, ...patch } : r)));

  const projectOf = (appId: string) => projects.find((p) => p._id === appId);

  const save = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const projectsPayload: UserGroupProjectInput[] = [
        ...rows.map((r) => (r.remove
          ? { appId: r.appId, _id: r._id, remove: true }
          : { appId: r.appId, _id: r._id, blocked: r.blocked })),
        ...addAppIds.map((appId) => ({ appId })),
      ];
      const payload: any = { projects: projectsPayload };
      // Contact is editable only for standalone users (linked users derive it from personnel).
      if (!isLinked && email.trim()) payload.contact = { email: email.trim(), phoneNo: phone.trim() };
      if (language !== '') payload.defaultlanguageId = Number(language);

      const res = await usersService.updateGroup(id, payload);
      const failed = res.failed || [];
      const projName = (aid: string) => projects.find((p) => p._id === aid)?.name || aid;
      const changed = res.updated.length + res.created.length + res.deleted.length;
      if (failed.length === 0) {
        toast.current?.show({ severity: 'success', summary: 'Saved', detail: `Applied across ${changed} project${changed === 1 ? '' : 's'}.`, life: 3000 });
        setTimeout(() => navigate(`/user-management/group/${id}`), 800);
      } else {
        toast.current?.show({
          severity: changed > 0 ? 'warn' : 'error',
          summary: changed > 0 ? 'Saved with some failures' : 'Save failed',
          detail: `${changed} applied, ${failed.length} failed: ${failed.map((f) => `${projName(f.appId)} (${f.reason})`).join('; ')}`,
          sticky: true,
        });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(err, 'Failed to save'), life: 8000 });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageSkeleton />;
  if (!group) return null;

  return (
    <PermissionGuard jobName={JOB_NAMES.USER_MANAGEMENT}>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-UserManagement-GroupForm">
        <ScreenHeader
          icon="pi pi-users"
          title={`Edit ${group.userId || 'User'}`}
          subtitle="Shared details apply to every project; status is per project."
          onBack={() => navigate(`/user-management/group/${id}`)}
        />

        {/* Shared identity */}
        <div className="ds-card p-4 mb-3">
          <h2 className="text-base font-semibold text-ink mb-3">Identity (applied to all projects)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput name="userId" label="Username" value={group.userId || ''} onChange={() => undefined} disabled helperText="Immutable — same in every project." />
            {isLinked ? (
              <FormInput name="personnel" label="Personnel" value={personnelName} onChange={() => undefined} disabled helperText="Email & phone come from the personnel." />
            ) : (
              <>
                <FormInput name="email" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
                <PhoneField name="phone" label="Phone" value={phone} onChange={setPhone} />
              </>
            )}
            <FormSelect name="language" label="Default Language" value={language} onChange={(e) => setLanguage(e.target.value)} options={LANGUAGE_OPTIONS} helperText="Language the user sees in the app." />
          </div>
        </div>

        {/* Projects */}
        <div className="ds-card p-4 mb-3">
          <h2 className="text-base font-semibold text-ink mb-3">Projects</h2>

          <div className="mb-4">
            <FormMultiSelect
              name="addProjects"
              label="Add to projects"
              value={addAppIds}
              onChange={setAddAppIds}
              options={addOptions}
              disabled={addOptions.length === 0}
              helperText="Adds this user to more projects (same username & PIN)."
            />
          </div>

          <div className="rounded-lg border border-line overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-4 py-2 bg-gray-100/70 dark:bg-gray-800/50 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              <span>Project</span>
              <span className="w-40 text-center">Access</span>
              <span className="w-28 text-right">Membership</span>
            </div>
            <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((r) => {
                const proj = projectOf(r.appId);
                return (
                  <div key={r._id} className={`grid grid-cols-[1fr_auto_auto] items-center gap-4 px-4 py-3 transition-colors ${r.remove ? 'bg-rose-50/60 dark:bg-rose-900/10' : ''}`}>
                    {/* Project — light, not a heavy pill. */}
                    <div className="min-w-0">
                      <span className={`text-sm font-medium ${r.remove ? 'line-through text-ink-muted' : 'text-ink'}`}>
                        {proj?.name || r.appId}
                      </span>
                      {proj?.appCode && <span className="ml-1.5 text-xs text-ink-muted">({proj.appCode})</span>}
                    </div>

                    {/* Access — Active / Blocked, self-explanatory. */}
                    <div className="w-40 flex items-center justify-center gap-2" title="Whether this user can sign in to this project.">
                      <InputSwitch checked={!r.blocked} onChange={(e: any) => setRow(r.appId, { blocked: !e.value })} disabled={r.remove} />
                      <span className={`text-xs font-medium ${r.blocked ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {r.blocked ? 'Blocked' : 'Active'}
                      </span>
                    </div>

                    {/* Membership — remove/keep this project's account. */}
                    <div className="w-28 flex justify-end">
                      {r.remove ? (
                        <button type="button" onClick={() => setRow(r.appId, { remove: false })}
                          className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-xs text-ink-muted hover:bg-canvas">
                          <i className="pi pi-undo text-[10px]" /> Undo
                        </button>
                      ) : (
                        <button type="button" onClick={() => setRow(r.appId, { remove: true })}
                          className="inline-flex items-center gap-1 rounded-md border border-rose-200 dark:border-rose-800 px-2 py-1 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                          title="Removes this user's account in this project on save (soft delete). They stay in their other projects.">
                          <i className="pi pi-trash text-[10px]" /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="mt-2 text-xs text-ink-muted">
            <strong>Access</strong> blocks/allows sign-in to that project. <strong>Remove</strong> deletes the user's account there on save — they remain in their other projects.
          </p>
        </div>

        {/* Actions */}
        <div className="ds-card px-4 py-3 flex gap-3 justify-end">
          <Button label="Cancel" severity="secondary" outlined onClick={() => navigate(`/user-management/group/${id}`)} disabled={saving} />
          <Button label={saving ? 'Saving...' : 'Save'} icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} onClick={save} disabled={saving} />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default UserManagementGroupForm;
